import { FC } from 'react';
import ReactSelect, {
  GroupBase,
  MenuPlacement,
  OptionsOrGroups,
  PropsValue,
  SelectComponentsConfig,
} from 'react-select';
import './ReactSelect.scss';

import classNames from 'classnames';

export {
  components,
} from 'react-select';

export type {
  OptionProps,
} from 'react-select';

export type OptionType = {
  value: string | number;
  label: string;
  [x: string]: any;
};

export type SelectProps = {
  options: OptionsOrGroups<OptionType, GroupBase<OptionType>> | undefined;
  defaultValue?: PropsValue<OptionType>;
  onChange?: (value: PropsValue<OptionType>) => void | undefined;
  isLoading?: boolean;
  isMulti?: boolean;
  value?: PropsValue<OptionType> | undefined;
  placement?: MenuPlacement;
  isDisabled?: boolean;
  isSearchable?: boolean;
  validationErrorMessage?: string | null;
  idContainer?: string;
  inputId?: string;
  components?: SelectComponentsConfig<OptionType, boolean, GroupBase<OptionType>>;
};

/**
 * This component wraps a react-select component, which enables users to select option(s) from a list.
 *
 * @prop options                   Array of options that populate the select menu
 * @prop defaultValue              Default input value
 * @prop onChange                  Function is called when the value of the input changes
 * @prop isLoading                 Value showing whether data is loading or not
 * @prop isMulti                   Support multiple selected options
 * @prop value                     Current value of the select
 * @prop placement                 Where the menu opens relative to the button: 'auto' | 'bottom' | 'top'
 * @prop isDisabled                Boolean that disables the select component if set to true
 * @prop isSearchable              Boolean that enables list filtering
 * @prop validationErrorMessage    Additional errorMessage for the form events
 * @prop idContainer               Container Id for testing purposes
 * @prop inputId                   Id for the underlying input element, keep it unique when using multiple selects on same page
 */

const Select: FC<SelectProps> = ({
  onChange,
  defaultValue,
  isLoading,
  options,
  isMulti,
  value,
  placement,
  isDisabled,
  isSearchable = false, // disables editing the dropdown text -- more like a standard select
  validationErrorMessage,
  idContainer,
  inputId,
  components = {},
}: SelectProps): JSX.Element => {
  const initialValue = (!defaultValue && options?.[0] ? options[0] : defaultValue) as PropsValue<OptionType>;
  const invalidClassName = classNames({ 'ReactSelect--invalid': validationErrorMessage });

  return (
    <>
      <ReactSelect
        classNamePrefix="CustomSelect"
        className={`ReactSelect SelectDropdown ${invalidClassName}`}
        isDisabled={isDisabled}
        value={value}
        isMulti={isMulti}
        onChange={onChange}
        defaultValue={initialValue}
        isLoading={isLoading}
        options={options}
        menuPlacement={placement}
        isSearchable={isSearchable}
        components={{
          IndicatorSeparator: (): null => null,
          ...(components ? components : {}),
        }}
        id={idContainer}
        inputId={inputId}
      />
      {validationErrorMessage && <p className="ReactSelect__ErrorMessage">{validationErrorMessage}</p>}
    </>
  );
};

export default Select;
